import React, { useEffect, useRef, useState } from "react";
import { Divider, Form, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../../features/save/saveSlice";
import { setActiveObj } from "../../../../features/obj/objSlice";
import { fetchCrops, fetchData } from "../../../../features/fetch/fetchSlice";
import SchemeDetails from "../sections/SchemeDetails";
import Production from "../sections/Production";

function ProductionStep({ handleNext }) {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const urlScheme = `${process.env.REACT_APP_API_BASE_SCHEME}/sch/get_schemes`;
  const urlVariety = `${process.env.REACT_APP_API_BASE_ROBOT}/crop_variety/fetch_cropvarietys`;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { saving } = useSelector((state) => state.save);
  const { crops } = useSelector((state) => state.fetch);
  const { activeObjProductionData, dataScheme, dataVariety } = useSelector(
    (state) => ({
      activeObjProductionData: state.obj.activeObj["schemeProductionData"],
      dataScheme: state.fetch.data[urlScheme] || [],
      dataVariety: state.fetch.data[urlVariety] || [],
    })
  );

  const [selectedScheme, setSelectedScheme] = useState(null);

  const isEdit = activeObjProductionData?.id ? true : false;

  function handleCancel() {
    navigate("/production-data");
  }

  const handleSchemeChange = (value) => {
    const scheme = dataScheme?.find((item) => item?.schId === value);
    setSelectedScheme(scheme);
    form.setFieldsValue({
      countyName: scheme?.schCountyName ?? null,
      commandArea: scheme?.schCommandArea ?? null,
      potentialAcreage: scheme?.schPotentialSize ?? null,
      developedArea: scheme?.schLandSize ?? null,
      irrigatedArea: scheme?.schIrrigationArea ?? null,
    });
  };

  const onFinish = async (data) => {
    const { schemeId, cropId, varietyIds, ...rest } = data;

    const crop = crops?.find((item) => item?.cropId === cropId);

    const payload = {
      ...rest,
      id: activeObjProductionData?.id ?? null,
      scheme: schemeId
        ? { id: schemeId, name: selectedScheme?.schName ?? null }
        : null,
      crop: cropId ? { id: cropId, name: crop?.cropName ?? null } : null,
      varieties: Array.isArray(varietyIds)
        ? varietyIds?.map((id) => ({
            id: id,
            name:
              dataVariety?.find((item) => item?.cvId === id)?.cvVariety ??
              null,
          }))
        : [],
    };

    const res = await dispatch(
      save({
        ...payload,
        url: "api/v1/scheme-production-data/save",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);

      // The varieties step needs the record id, so carry the saved record forward
      const saved = Array.isArray(res?.payload?.data?.result)
        ? res?.payload?.data?.result?.[0]
        : res?.payload?.data?.result;

      await dispatch(
        setActiveObj({
          key: "schemeProductionData",
          data: {
            ...activeObjProductionData,
            ...payload,
            ...(saved ?? {}),
          },
        })
      );
      await handleNext();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    dispatch(fetchCrops());
    dispatch(
      fetchData({
        url: urlScheme,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlVariety,
        limit: 1000,
        start: 0,
      })
    );
  }, []);

  useEffect(() => {
    if (!activeObjProductionData?.id) {
      return;
    }
    form.setFieldsValue({
      ...activeObjProductionData,
      schemeId: activeObjProductionData?.scheme?.id ?? null,
      financialYear:
        activeObjProductionData?.financialYear?.name ??
        activeObjProductionData?.financialYear ??
        null,
      cropId: activeObjProductionData?.crop?.id ?? null,
      varietyIds: activeObjProductionData?.varieties?.map((item) => item?.id),
    });
  }, [activeObjProductionData, form]);

  useEffect(() => {
    if (activeObjProductionData?.scheme?.id && dataScheme?.length > 0) {
      setSelectedScheme(
        dataScheme?.find(
          (item) => item?.schId === activeObjProductionData?.scheme?.id
        )
      );
    }
  }, [activeObjProductionData, dataScheme]);

  return (
    <>
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
      >
        <SchemeDetails
          dataScheme={dataScheme}
          crops={crops}
          isEdit={isEdit}
          handleSchemeChange={handleSchemeChange}
        />

        <Production dataVariety={dataVariety} />
      </Form>

      <div className="sticky bottom-0 left-0 w-full bg-white">
        <Divider />
        <div className="flex justify-between mt-[1rem] gap-x-[1.25rem]">
          <div className="w-[100px]">
            <button
              type="button"
              key="back"
              onClick={handleCancel}
              className="cstm-btn"
            >
              Cancel
            </button>
          </div>

          <div className="w-[100px]">
            <button
              onClick={() => form.submit()}
              disabled={saving}
              type="button"
              className="cstm-btn-2"
            >
              {saving ? <Spin /> : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductionStep;
